import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { t } from '../i18n'; // Import new auto-translation function
import ProductDetail from './ProductDetail';
import { products, getShortDescription } from './ProductData';
import './Products.css';

// Custom hook for fade-in animations
function useFadeInOnScroll() {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight - 100) {
        el.classList.add('visible');
      }
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return ref;
}

// Define the content for different languages
const bgContent = {
  hero: {
    title: 'Нашите Продукти',
    subtitle: 'Висококачествени продукти за естетическа медицина и регенеративна терапия'
  }
};

const Products = ({ language = 'bg' }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || 'all';
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const navigate = useNavigate();

  // Animation refs
  const filterRef = useFadeInOnScroll();
  const gridRef = useFadeInOnScroll();

  // Set page title for SEO
  useEffect(() => {
    document.title = `${t('Продукти', language)} - EXOREVIVE BULGARIA`;
    
    // Add meta description for SEO
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t('Висококачествени продукти за естетическа медицина и регенеративна терапия от EXOREVIVE BULGARIA - екзозоми, PRP, биорепаранти.', language));
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = t('Висококачествени продукти за естетическа медицина и регенеративна терапия от EXOREVIVE BULGARIA - екзозоми, PRP, биорепаранти.', language);
      document.head.appendChild(meta);
    }
  }, [language]);

  const categories = [
    { 
      key: 'all', 
      name: t('Всички продукти', language)
    },
    { 
      key: 'rejuvenation', 
      name: t('Подмладяване', language)
    },
    { 
      key: 'hair', 
      name: t('Коса', language)
    },
    { 
      key: 'skincare', 
      name: t('Грижа за кожата', language)
    },
    { 
      key: 'exosomes', 
      name: t('Екзозоми', language)
    }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.categoryKey === selectedCategory);

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`);
  };

  if (selectedProduct) {
    return <ProductDetail product={selectedProduct} language={language} />;
  }

  return (
    <div className="products-page">
      {/* Filter Section */}
      <section className="products-filter" ref={filterRef}>
        <div className="products-container">
          <div className="filter-content">
            <h3 className="section-label">{t('ФИЛТРИРАЙТЕ', language)}</h3>
            <h2>{t('Изберете категория', language)}</h2>
          </div>
          
          <div className="category-buttons">
            {categories.map((category) => (
              <button
                key={category.key}
                className={`category-btn ${selectedCategory === category.key ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.key)}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="products-grid-section" ref={gridRef}>
        <div className="products-container">
          <div className="products-grid">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="product-card"
                onClick={() => handleProductClick(product)}
              >
                <div className="product-image-container">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="product-image"
                  />
                  <div className="product-overlay">
                    <button className="view-details-btn">
                      {t('Преглед', language)}
                    </button>
                  </div>
                </div>
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">{getShortDescription(product.id, language)}</p>
                  <div className="product-category">
                    {product.category.split(', ').map((cat, index) => (
                      <span key={index} className="product-category-tag">
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="no-products">
              <h3>{t('Няма намерени продукти', language)}</h3>
              <p>{t('Опитайте с друга категория или вижте всички продукти.', language)}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products; 